// repo/primary-care-connect/frontend/src/pages/Portal/FamilyDoctor/NewPatientRequests/NewPatientRequestsPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./NewPatientRequestsPage.css";

import { downloadReportPdf } from "../../../../utils/reportExport";
import type { Report } from "../../../../utils/reportExport";

import {
  ApiError,
  RequestsListItem,
  listFamilyDoctorRequests,
} from "../../../../api/requests";

type SourceFilter = "all" | "public" | "navigator";

type PatientBaselineFilter = "all" | "unattached" | "attached";

type StatusGroupFilter =
  | "all"
  | "submittedReview"
  | "waitlisted"
  | "invited"
  | "attached"
  | "closed";

type SortKey =
  | "requestId"
  | "name"
  | "submitted"
  | "source"
  | "patientBaseline"
  | "priority"
  | "currentStatus"
  | "attachmentDate"
  | "tta";

type SortDirection = "asc" | "desc";

type SortState = {
  key: SortKey;
  direction: SortDirection;
};

type StatusCounts = Record<StatusGroupFilter, number>;

type PatientBaselineKey = "unattached" | "attached" | "unknown";

const CLOSED_STATUS_KEYS = new Set([
  "declined",
  "closedpatientwithdrew",
  "closedacceptedelsewhere",
  "closednoshow",
  "closedclinicdeclinedaftermeetgreet",
  "cancelled",
  "expired",
]);

function asText(value: unknown): string {
  return String(value ?? "").trim();
}

function asLower(value: unknown): string {
  return asText(value).toLowerCase();
}

function toTimestamp(value: unknown): number | null {
  const raw = asText(value);
  if (!raw) return null;

  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : null;
}

function formatDate(value: unknown): string {
  const raw = asText(value);
  if (!raw) return "—";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(value: unknown): string {
  const raw = asText(value);
  if (!raw) return "—";

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function normalizeStatus(status: unknown): string {
  const raw = asText(status);
  if (!raw) return "";

  const key = raw.toLowerCase();

  if (key === "in_review") return "InReview";
  if (key === "notaccepted") return "Declined";

  if (key === "attachment_complete" || key === "attachmentcomplete") {
    return "AttachmentComplete";
  }

  if (key === "closedpatientwithdrew") return "ClosedPatientWithdrew";
  if (key === "closedacceptedelsewhere") return "ClosedAcceptedElsewhere";
  if (key === "closednoshow") return "ClosedNoShow";

  if (key === "closedclinicdeclinedaftermeetgreet") {
    return "ClosedClinicDeclinedAfterMeetGreet";
  }

  return raw;
}

function currentStatusLabel(status: unknown): string {
  const normalized = normalizeStatus(status);
  const key = normalized.toLowerCase();

  if (key === "submitted") return "Submitted";
  if (key === "inreview") return "Under Review";
  if (key === "waitlist") return "Waitlisted";
  if (key === "accepted") return "Invited";
  if (key === "attachmentcomplete") return "Attached";
  if (key === "declined") return "Declined";
  if (key === "closedpatientwithdrew") return "Closed — Patient Withdrew";
  if (key === "closedacceptedelsewhere") return "Closed — Accepted Elsewhere";
  if (key === "closednoshow") return "Closed — No Show";

  if (key === "closedclinicdeclinedaftermeetgreet") {
    return "Closed — Clinic Declined After Meet & Greet";
  }

  if (key === "cancelled") return "Cancelled";
  if (key === "expired") return "Expired";

  return normalized || "Unknown";
}

function statusGroupForRequest(item: RequestsListItem): StatusGroupFilter {
  const statusKey = normalizeStatus(item.status).toLowerCase();

  if (statusKey === "submitted" || statusKey === "inreview") {
    return "submittedReview";
  }

  if (statusKey === "waitlist") return "waitlisted";
  if (statusKey === "accepted") return "invited";
  if (statusKey === "attachmentcomplete") return "attached";
  if (CLOSED_STATUS_KEYS.has(statusKey)) return "closed";

  return "submittedReview";
}

function statusGroupLabel(filter: StatusGroupFilter): string {
  if (filter === "all") return "All";
  if (filter === "submittedReview") return "Submitted";
  if (filter === "waitlisted") return "Waitlisted";
  if (filter === "invited") return "Invited";
  if (filter === "attached") return "Attached";
  if (filter === "closed") return "Closed";
  return "All";
}

function statusPillClass(status: unknown): string {
  const key = normalizeStatus(status).toLowerCase();

  if (key === "submitted" || key === "inreview") {
    return "ocf-status ocf-status--pending";
  }

  if (key === "waitlist") return "ocf-status ocf-status--waitlist";
  if (key === "accepted") return "ocf-status ocf-status--accepted";
  if (key === "attachmentcomplete") return "ocf-status ocf-status--accepted";

  if (key === "declined") return "ocf-status ocf-status--declined";

  if (CLOSED_STATUS_KEYS.has(key)) {
    return "ocf-status ocf-status--muted";
  }

  return "ocf-status";
}

function patientDisplayName(patient: any): string {
  if (!patient) return "Unnamed patient";

  const first = asText(patient.firstName);
  const last = asText(patient.lastName);
  const composed = [first, last].filter(Boolean).join(" ").trim();

  if (composed) return composed;

  const legacy = asText(patient.fullName || patient.name);
  return legacy || "Unnamed patient";
}

function patientPostalCode(item: RequestsListItem): string {
  return (
    asText((item as any)?.patient?.postalCode) ||
    asText((item as any)?.postalCode) ||
    "—"
  );
}

function submittedAt(item: RequestsListItem): string | null {
  const timeline = (item as any)?.timeline || {};

  return (
    asText(timeline.submittedAt) ||
    asText(timeline.createdAt) ||
    asText((item as any)?.submittedAt) ||
    asText((item as any)?.createdAt) ||
    null
  );
}

function attachmentCompletedAt(item: RequestsListItem): string | null {
  const timeline = (item as any)?.timeline || {};

  return (
    asText(timeline.attachmentCompletedAt) ||
    asText((item as any)?.attachmentCompletedAt) ||
    null
  );
}

function ttaDays(item: RequestsListItem): number | null {
  const start = toTimestamp(submittedAt(item));
  const end = toTimestamp(attachmentCompletedAt(item));

  if (start === null || end === null) return null;
  if (end < start) return null;

  const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  return Math.max(days, 0);
}

function formatTta(item: RequestsListItem): string {
  const days = ttaDays(item);
  if (days === null) return "—";
  if (days === 0) return "Same day";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function patientBaselineKey(item: RequestsListItem): PatientBaselineKey {
  const baseline = asLower((item as any)?.patientStatusBeforeRequest);

  if (baseline === "unattached") return "unattached";
  if (baseline === "attached") return "attached";

  return "unknown";
}

function patientBaselineLabel(item: RequestsListItem): string {
  const baseline = patientBaselineKey(item);

  if (baseline === "unattached") return "Unattached";
  if (baseline === "attached") return "Attached / Switching";

  return "Unknown";
}

function patientBaselineFilterLabel(filter: PatientBaselineFilter): string {
  if (filter === "unattached") return "Unattached";
  if (filter === "attached") return "Attached / Switching";
  return "All";
}

function patientBaselineSortValue(item: RequestsListItem): number {
  const baseline = patientBaselineKey(item);

  if (baseline === "unattached") return 1;
  if (baseline === "attached") return 2;

  return 3;
}

function patientBaselineClass(item: RequestsListItem): string {
  const baseline = patientBaselineKey(item);

  if (baseline === "unattached") {
    return "ocf-baseline-pill ocf-baseline-pill--unattached";
  }

  if (baseline === "attached") {
    return "ocf-baseline-pill ocf-baseline-pill--attached";
  }

  return "ocf-baseline-pill ocf-baseline-pill--unknown";
}

function isNavigatorRequest(item: RequestsListItem): boolean {
  const anyItem = item as any;

  const sourceType = asLower(anyItem.sourceType || anyItem.request?.sourceType);
  const sourceLabel = asLower(anyItem.sourceLabel || anyItem.request?.sourceLabel);
  const source = asLower(anyItem.source || anyItem.request?.source);

  const navigatorCaseId =
    anyItem.navigatorCaseId ??
    anyItem.navigatorCaseID ??
    anyItem.request?.navigatorCaseId ??
    anyItem.request?.navigatorCaseID ??
    null;

  if (navigatorCaseId !== null && navigatorCaseId !== undefined) return true;

  if (sourceType.includes("navigator")) return true;
  if (sourceType === "oht") return true;
  if (sourceType === "futurehcc") return true;
  if (sourceType === "manualnavigatorentry") return true;

  if (sourceLabel.includes("navigator")) return true;
  if (source.includes("navigator")) return true;

  return false;
}

function sourceLabel(item: RequestsListItem): string {
  return isNavigatorRequest(item) ? "Navigator" : "Public";
}

function sourceClass(item: RequestsListItem): string {
  return isNavigatorRequest(item)
    ? "ocf-source-pill ocf-source-pill--navigator"
    : "ocf-source-pill ocf-source-pill--public";
}

function normalizePriorityLabel(value: unknown): string | null {
  const raw = asText(value);
  if (!raw) return null;

  const compact = raw.toLowerCase().replace(/[\s_-]+/g, "");

  if (compact === "urgent" || compact.includes("priorityurgent")) {
    return "Urgent";
  }

  if (compact === "high" || compact.includes("priorityhigh")) {
    return "High";
  }

  if (
    compact === "medium" ||
    compact === "moderate" ||
    compact.includes("prioritymedium") ||
    compact.includes("prioritymoderate")
  ) {
    return "Medium";
  }

  if (compact === "low" || compact.includes("prioritylow")) {
    return "Low";
  }

  if (
    compact === "routine" ||
    compact === "standard" ||
    compact.includes("priorityroutine") ||
    compact.includes("prioritystandard")
  ) {
    return "Routine";
  }

  return null;
}

function priorityRawValue(item: RequestsListItem): string {
  const anyItem = item as any;

  const directCandidates = [
    anyItem.navigatorPriorityLevel,
    anyItem.NavigatorPriorityLevel,
    anyItem.navigatorPriority,
    anyItem.NavigatorPriority,
    anyItem.priorityLevel,
    anyItem.PriorityLevel,
    anyItem.priority,
    anyItem.Priority,
    anyItem.request?.navigatorPriorityLevel,
    anyItem.request?.NavigatorPriorityLevel,
    anyItem.request?.navigatorPriority,
    anyItem.request?.NavigatorPriority,
    anyItem.request?.priorityLevel,
    anyItem.request?.PriorityLevel,
    anyItem.request?.priority,
    anyItem.request?.Priority,
    anyItem.navigator?.priorityLevel,
    anyItem.navigator?.PriorityLevel,
    anyItem.navigator?.priority,
    anyItem.navigator?.Priority,
    anyItem.navigator?.navigatorPriorityLevel,
    anyItem.navigator?.NavigatorPriorityLevel,
    anyItem.navigatorContext?.priorityLevel,
    anyItem.navigatorContext?.PriorityLevel,
    anyItem.navigatorContext?.priority,
    anyItem.navigatorContext?.Priority,
  ];

  for (const candidate of directCandidates) {
    const normalized = normalizePriorityLabel(candidate);
    if (normalized) return normalized;
  }

  const priorityTags = Array.isArray(anyItem.priorityTags)
    ? anyItem.priorityTags
    : [];

  for (const tag of priorityTags) {
    const normalized =
      normalizePriorityLabel(tag?.key) ||
      normalizePriorityLabel(tag?.label) ||
      normalizePriorityLabel(tag?.value);

    if (normalized) return normalized;
  }

  return "";
}

function priorityLabel(item: RequestsListItem): string {
  const raw = priorityRawValue(item);

  return raw || "Routine";
}

function prioritySortValue(item: RequestsListItem): number {
  const label = priorityLabel(item).toLowerCase();

  if (label === "urgent") return 1;
  if (label === "high") return 2;
  if (label === "medium") return 3;
  if (label === "routine") return 4;
  if (label === "low") return 5;

  return 6;
}

function priorityClass(item: RequestsListItem): string {
  const label = priorityLabel(item).toLowerCase();

  if (label === "urgent") return "ocf-priority-pill ocf-priority-pill--urgent";
  if (label === "high") return "ocf-priority-pill ocf-priority-pill--high";
  if (label === "medium") return "ocf-priority-pill ocf-priority-pill--medium";
  if (label === "low") return "ocf-priority-pill ocf-priority-pill--low";

  return "ocf-priority-pill ocf-priority-pill--routine";
}

function sourceMatchesFilter(
  item: RequestsListItem,
  sourceFilter: SourceFilter,
): boolean {
  if (sourceFilter === "all") return true;
  if (sourceFilter === "navigator") return isNavigatorRequest(item);
  return !isNavigatorRequest(item);
}

function baselineMatchesFilter(
  item: RequestsListItem,
  baselineFilter: PatientBaselineFilter,
): boolean {
  if (baselineFilter === "all") return true;
  return patientBaselineKey(item) === baselineFilter;
}

function statusMatchesFilter(
  item: RequestsListItem,
  statusFilter: StatusGroupFilter,
): boolean {
  if (statusFilter === "all") return true;
  return statusGroupForRequest(item) === statusFilter;
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function compareNullableNumber(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

function getSortValue(item: RequestsListItem, key: SortKey): string | number | null {
  if (key === "requestId") return Number(item.requestId) || 0;
  if (key === "name") return patientDisplayName((item as any).patient);
  if (key === "submitted") return toTimestamp(submittedAt(item));
  if (key === "source") return sourceLabel(item);
  if (key === "patientBaseline") return patientBaselineSortValue(item);
  if (key === "priority") return prioritySortValue(item);
  if (key === "currentStatus") return currentStatusLabel(item.status);
  if (key === "attachmentDate") return toTimestamp(attachmentCompletedAt(item));
  if (key === "tta") return ttaDays(item);

  return null;
}

function sortRequests(
  list: RequestsListItem[],
  sortState: SortState,
): RequestsListItem[] {
  const sorted = [...list];

  sorted.sort((a, b) => {
    const aValue = getSortValue(a, sortState.key);
    const bValue = getSortValue(b, sortState.key);

    let result = 0;

    if (typeof aValue === "string" || typeof bValue === "string") {
      result = compareText(asText(aValue), asText(bValue));
    } else {
      result = compareNullableNumber(
        typeof aValue === "number" ? aValue : null,
        typeof bValue === "number" ? bValue : null,
      );
    }

    if (result === 0) {
      result = Number(a.requestId || 0) - Number(b.requestId || 0);
    }

    return sortState.direction === "asc" ? result : -result;
  });

  return sorted;
}

function sortButtonLabel(label: string, key: SortKey, sortState: SortState) {
  if (sortState.key !== key) return label;
  return `${label} ${sortState.direction === "asc" ? "↑" : "↓"}`;
}

function sourceLabelForFilter(sourceFilter: SourceFilter): string {
  if (sourceFilter === "public") return "Public";
  if (sourceFilter === "navigator") return "Navigator";
  return "All";
}

function exportFilename(): string {
  const stamp = new Date()
    .toISOString()
    .slice(0, 16)
    .replace(/[-:T]/g, "");

  return `family-doctor-patient-requests-${stamp}`;
}

function buildRequestsExportReport({
  rows,
  sourceFilter,
  baselineFilter,
  statusFilter,
  search,
}: {
  rows: RequestsListItem[];
  sourceFilter: SourceFilter;
  baselineFilter: PatientBaselineFilter;
  statusFilter: StatusGroupFilter;
  search: string;
}): Report {
  const generatedAt = new Date().toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  const filterSummary = [
    `Source: ${sourceLabelForFilter(sourceFilter)}`,
    `Patient Baseline: ${patientBaselineFilterLabel(baselineFilter)}`,
    `Status: ${statusGroupLabel(statusFilter)}`,
    search.trim() ? `Search: “${search.trim()}”` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const sourceCounts = rows.reduce(
    (counts, item) => {
      if (isNavigatorRequest(item)) {
        counts.navigator += 1;
      } else {
        counts.public += 1;
      }

      return counts;
    },
    { public: 0, navigator: 0 },
  );

  const baselineCounts = rows.reduce(
    (counts, item) => {
      const baseline = patientBaselineKey(item);
      if (baseline === "unattached") counts.unattached += 1;
      if (baseline === "attached") counts.attached += 1;
      if (baseline === "unknown") counts.unknown += 1;
      return counts;
    },
    { unattached: 0, attached: 0, unknown: 0 },
  );

  const priorityCounts = rows.reduce(
    (counts, item) => {
      const label = priorityLabel(item);
      counts[label] = (counts[label] || 0) + 1;
      return counts;
    },
    {} as Record<string, number>,
  );

  return {
    pageLabel: "Family Doctor Portal — Patient Requests",
    subtitle: `${filterSummary} · Generated ${generatedAt}`,
    chapters: [
      {
        title: "Export Summary",
        summary:
          "This PDF includes the full current filtered request selection from the Family Doctor Portal. Pagination affects only the on-screen table, not this export.",
        sections: [
          {
            type: "kpi",
            heading: "Selection Overview",
            items: [
              {
                label: "Requests exported",
                value: String(rows.length),
                hint: "Full filtered selection",
                tone: "blue",
              },
              {
                label: "Public requests",
                value: String(sourceCounts.public),
                hint: "Direct patient submissions",
                tone: "green",
              },
              {
                label: "Navigator requests",
                value: String(sourceCounts.navigator),
                hint: "Navigator-assisted submissions",
                tone: "amber",
              },
              {
                label: "Unattached baseline",
                value: String(baselineCounts.unattached),
                hint: "MOH-style unattached reason",
                tone: "grey",
              },
            ],
          },
          {
            type: "table",
            heading: "Applied Filters",
            columns: ["Filter", "Value"],
            rows: [
              ["Source", sourceLabelForFilter(sourceFilter)],
              ["Patient Baseline", patientBaselineFilterLabel(baselineFilter)],
              ["Status", statusGroupLabel(statusFilter)],
              ["Search", search.trim() || "—"],
              ["Generated", generatedAt],
            ],
            colWeights: [1, 3],
          },
          {
            type: "table",
            heading: "Priority Breakdown",
            columns: ["Priority", "Requests"],
            rows: Object.entries(priorityCounts)
              .sort(([a], [b]) => compareText(a, b))
              .map(([priority, count]) => [priority, count]),
            aligns: ["left", "right"],
            colWeights: [2, 1],
          },
        ],
      },
      {
        title: "Patient Requests",
        summary:
          rows.length > 0
            ? "Rows are shown in the same filtered and sorted order as the on-screen list."
            : "No requests matched the current selection.",
        sections: [
          {
            type: "table",
            heading: "Request List",
            columns: [
              "Request #",
              "Name",
              "Postal Code",
              "Submitted",
              "Source",
              "Patient Baseline",
              "Priority",
              "Current Status",
              "Attachment Date",
              "Time to Attachment",
            ],
            rows: rows.map((item) => {
              const submitted = submittedAt(item);
              const attachedAt = attachmentCompletedAt(item);

              return [
                `#${item.requestId}`,
                patientDisplayName((item as any).patient),
                patientPostalCode(item),
                formatDate(submitted),
                sourceLabel(item),
                patientBaselineLabel(item),
                priorityLabel(item),
                currentStatusLabel(item.status),
                formatDate(attachedAt),
                formatTta(item),
              ];
            }),
            colWeights: [0.8, 1.7, 1, 1.1, 0.95, 1.35, 0.9, 1.5, 1.2, 1.25],
          },
        ],
      },
    ],
  };
}

function exportRequestsToPdfFile({
  rows,
  sourceFilter,
  baselineFilter,
  statusFilter,
  search,
}: {
  rows: RequestsListItem[];
  sourceFilter: SourceFilter;
  baselineFilter: PatientBaselineFilter;
  statusFilter: StatusGroupFilter;
  search: string;
}) {
  const report = buildRequestsExportReport({
    rows,
    sourceFilter,
    baselineFilter,
    statusFilter,
    search,
  });

  downloadReportPdf(report, exportFilename());
}

const PAGE_SIZE = 20;

function formatResultsRange(
  totalFiltered: number,
  currentPage: number,
  pageSize: number,
): string {
  if (totalFiltered <= 0) return "0 requests shown";

  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalFiltered);

  return `${start}–${end} of ${totalFiltered} request${
    totalFiltered === 1 ? "" : "s"
  } shown`;
}

function getPaginationPages(
  currentPage: number,
  totalPages: number,
): Array<number | "ellipsis"> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: Array<number | "ellipsis"> = [1];

  if (currentPage > 3) {
    pages.push("ellipsis");
  }

  const rangeStart = Math.max(2, currentPage - 1);
  const rangeEnd = Math.min(totalPages - 1, currentPage + 1);

  for (let page = rangeStart; page <= rangeEnd; page += 1) {
    pages.push(page);
  }

  if (currentPage < totalPages - 2) {
    pages.push("ellipsis");
  }

  pages.push(totalPages);
  return pages;
}

async function fetchAllRequestsForUi() {
  return listFamilyDoctorRequests({
    status: "All",
    sourceScope: "all",
  });
}

export default function NewPatientRequestsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [items, setItems] = useState<RequestsListItem[]>([]);
  const [search, setSearch] = useState("");

  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [baselineFilter, setBaselineFilter] =
    useState<PatientBaselineFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusGroupFilter>("all");

  const [sortState, setSortState] = useState<SortState>({
    key: "submitted",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);

  async function refreshList() {
    setError(null);
    setLoading(true);

    try {
      const data = await fetchAllRequestsForUi();
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : "Failed to load requests";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sourceCounts = useMemo(() => {
    let publicCount = 0;
    let navigatorCount = 0;

    items.forEach((item) => {
      if (isNavigatorRequest(item)) {
        navigatorCount += 1;
      } else {
        publicCount += 1;
      }
    });

    return {
      all: items.length,
      public: publicCount,
      navigator: navigatorCount,
    };
  }, [items]);

  const sourceFilteredItems = useMemo(() => {
    return items.filter((item) => sourceMatchesFilter(item, sourceFilter));
  }, [items, sourceFilter]);

  const baselineCounts = useMemo(() => {
    let unattached = 0;
    let attached = 0;

    sourceFilteredItems.forEach((item) => {
      const baseline = patientBaselineKey(item);
      if (baseline === "unattached") unattached += 1;
      if (baseline === "attached") attached += 1;
    });

    return {
      all: sourceFilteredItems.length,
      unattached,
      attached,
    };
  }, [sourceFilteredItems]);

  const primaryFilteredItems = useMemo(() => {
    return sourceFilteredItems.filter((item) =>
      baselineMatchesFilter(item, baselineFilter),
    );
  }, [sourceFilteredItems, baselineFilter]);

  const statusCounts = useMemo<StatusCounts>(() => {
    const counts: StatusCounts = {
      all: primaryFilteredItems.length,
      submittedReview: 0,
      waitlisted: 0,
      invited: 0,
      attached: 0,
      closed: 0,
    };

    primaryFilteredItems.forEach((item) => {
      const group = statusGroupForRequest(item);
      counts[group] = (counts[group] || 0) + 1;
    });

    return counts;
  }, [primaryFilteredItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    const filteredItems = primaryFilteredItems.filter((item) => {
      if (!statusMatchesFilter(item, statusFilter)) return false;
      if (!q) return true;

      const patientName = patientDisplayName((item as any).patient);
      const firstName = asText((item as any).patient?.firstName);
      const lastName = asText((item as any).patient?.lastName);
      const postalCode = patientPostalCode(item);
      const requestId = String(item.requestId || "");

      const searchableText = [patientName, firstName, lastName, postalCode, requestId]
        .map(asText)
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(q);
    });

    return sortRequests(filteredItems, sortState);
  }, [primaryFilteredItems, statusFilter, search, sortState]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sourceFilter, baselineFilter, statusFilter, search, sortState]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const currentPageSafe = Math.min(Math.max(currentPage, 1), totalPages);

  const paginatedItems = useMemo(() => {
    const start = (currentPageSafe - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPageSafe]);

  const paginationPages = useMemo(
    () => getPaginationPages(currentPageSafe, totalPages),
    [currentPageSafe, totalPages],
  );

  function updateSort(key: SortKey) {
    setSortState((current) => {
      if (current.key !== key) {
        return {
          key,
          direction: key === "submitted" ? "desc" : "asc",
        };
      }

      return {
        key,
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  }

  function openRequestDetails(requestId: number) {
    if (!Number.isFinite(Number(requestId)) || Number(requestId) <= 0) return;
    navigate(`/portal/family-doctor/requests/${requestId}`);
  }

  const hasActiveFilters =
    sourceFilter !== "all" ||
    baselineFilter !== "all" ||
    statusFilter !== "all" ||
    search.trim().length > 0;

  return (
    <div className="ocf-requests-page">
      <div className="ocf-requests-header">
        <div>
          <div className="ocf-page-eyebrow">Intake queue</div>
          <h1 className="ocf-requests-title">Patient Requests</h1>
          <p className="ocf-requests-subtitle">
            Unified inbox for public and navigator-assisted patient requests.
          </p>
        </div>

        <div className="ocf-requests-actions">
          <button
            className="ocf-btn ocf-btn--secondary ocf-btn--icon"
            type="button"
            onClick={() =>
              exportRequestsToPdfFile({
                rows: filtered,
                sourceFilter,
                baselineFilter,
                statusFilter,
                search,
              })
            }
            disabled={loading || filtered.length === 0}
          >
            <span className="ocf-btn-icon ocf-btn-icon--export" aria-hidden="true" />
            Export
          </button>

          <button
            className="ocf-btn ocf-btn--secondary ocf-btn--icon"
            type="button"
            onClick={refreshList}
            disabled={loading}
            aria-busy={loading}
          >
            <span
              className={`ocf-btn-icon ocf-btn-icon--refresh${loading ? " is-spinning" : ""}`}
              aria-hidden="true"
            />
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="ocf-card ocf-inbox-card">
        <div className="ocf-inbox-toolbar">
          <div>
            <h2 className="ocf-inbox-title">Requests</h2>
            <p className="ocf-inbox-subtitle">
              {loading
                ? `${items.length} request${items.length === 1 ? "" : "s"} in inbox`
                : formatResultsRange(totalFiltered, currentPageSafe, PAGE_SIZE)}
            </p>
          </div>

          <div className="ocf-inbox-search-wrap">
            <label className="ocf-search-field">
              <span className="ocf-search-field-icon" aria-hidden="true" />
              <input
                className="ocf-input ocf-inbox-search"
                placeholder="Search patient name, request number, or postal code…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search patient requests"
              />
            </label>
          </div>
        </div>

        <div className="ocf-inbox-filters" aria-label="Filter requests">
          <div className="ocf-primary-filter-row" aria-label="Primary filters">
          <label className="ocf-filter-select-card ocf-filter-select-card--source">
            <span className="ocf-filter-select-label">Source</span>
            <select
              className="ocf-select ocf-primary-select"
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value as SourceFilter);
                setBaselineFilter("all");
                setStatusFilter("all");
              }}
            >
              <option value="all">All ({sourceCounts.all})</option>
              <option value="public">Public ({sourceCounts.public})</option>
              <option value="navigator">
                Navigator ({sourceCounts.navigator})
              </option>
            </select>
          </label>

          <label className="ocf-filter-select-card ocf-filter-select-card--baseline">
            <span className="ocf-filter-select-label">Patient Baseline</span>
            <select
              className="ocf-select ocf-primary-select"
              value={baselineFilter}
              onChange={(e) =>
                setBaselineFilter(e.target.value as PatientBaselineFilter)
              }
            >
              <option value="all">All ({baselineCounts.all})</option>
              <option value="unattached">
                Unattached — MOH ({baselineCounts.unattached})
              </option>
              <option value="attached">
                Attached / Switching ({baselineCounts.attached})
              </option>
            </select>
          </label>
        </div>

        <div className="ocf-filter-row ocf-status-filter-row" aria-label="Status filters">
          {(
            [
              "all",
              "submittedReview",
              "waitlisted",
              "invited",
              "attached",
              "closed",
            ] as StatusGroupFilter[]
          ).map((filter) => (
            <button
              key={filter}
              className={`ocf-filter-chip ${
                statusFilter === filter ? "is-active" : ""
              }`}
              type="button"
              onClick={() => setStatusFilter(filter)}
            >
              {statusGroupLabel(filter)} <span>{statusCounts[filter]}</span>
            </button>
          ))}
        </div>
        </div>

        {hasActiveFilters ? (
          <div className="ocf-active-filter-row">
            <span>
              Filters active
              {sourceFilter !== "all"
                ? ` · Source: ${sourceLabelForFilter(sourceFilter)}`
                : ""}
              {baselineFilter !== "all"
                ? ` · Baseline: ${patientBaselineFilterLabel(baselineFilter)}`
                : ""}
              {statusFilter !== "all"
                ? ` · Status: ${statusGroupLabel(statusFilter)}`
                : ""}
              {search.trim() ? ` · Search: “${search.trim()}”` : ""}
            </span>

            <button
              className="ocf-link-button"
              type="button"
              onClick={() => {
                setSourceFilter("all");
                setBaselineFilter("all");
                setStatusFilter("all");
                setSearch("");
              }}
            >
              Clear filters
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="ocf-inbox-empty ocf-inbox-empty--loading" role="status">
            <span className="ocf-state-icon ocf-state-icon--loading" aria-hidden="true" />
            <span className="ocf-state-title">Loading requests</span>
            <span className="ocf-state-message">Fetching the latest patient requests…</span>
          </div>
        ) : error ? (
          <div className="ocf-error" role="alert">
            <span className="ocf-state-icon ocf-state-icon--error" aria-hidden="true" />
            <span className="ocf-state-title">Unable to load requests</span>
            <span className="ocf-state-message">{error}</span>
            <div className="ocf-error-actions">
              <button className="ocf-btn" type="button" onClick={refreshList}>
                Try again
              </button>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="ocf-inbox-empty ocf-inbox-empty--no-results">
            <span className="ocf-state-icon ocf-state-icon--empty" aria-hidden="true" />
            <span className="ocf-state-title">No matching requests</span>
            <span className="ocf-state-message">
              No requests match the current filters.
            </span>
          </div>
        ) : (
          <div className="ocf-inbox-results">
          <div className="ocf-inbox-table-wrap">
            <table className="ocf-inbox-table">
              <thead>
                <tr>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "requestId" ? "is-sorted" : ""}
                      onClick={() => updateSort("requestId")}
                    >
                      {sortButtonLabel("#", "requestId", sortState)}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "name" ? "is-sorted" : ""}
                      onClick={() => updateSort("name")}
                    >
                      {sortButtonLabel("Name", "name", sortState)}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "submitted" ? "is-sorted" : ""}
                      onClick={() => updateSort("submitted")}
                    >
                      {sortButtonLabel("Submitted", "submitted", sortState)}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "source" ? "is-sorted" : ""}
                      onClick={() => updateSort("source")}
                    >
                      {sortButtonLabel("Source", "source", sortState)}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "patientBaseline" ? "is-sorted" : ""}
                      onClick={() => updateSort("patientBaseline")}
                    >
                      {sortButtonLabel(
                        "Patient Baseline",
                        "patientBaseline",
                        sortState,
                      )}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "priority" ? "is-sorted" : ""}
                      onClick={() => updateSort("priority")}
                    >
                      {sortButtonLabel("Priority", "priority", sortState)}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "currentStatus" ? "is-sorted" : ""}
                      onClick={() => updateSort("currentStatus")}
                    >
                      {sortButtonLabel(
                        "Current Status",
                        "currentStatus",
                        sortState,
                      )}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "attachmentDate" ? "is-sorted" : ""}
                      onClick={() => updateSort("attachmentDate")}
                    >
                      {sortButtonLabel(
                        "Attachment Date",
                        "attachmentDate",
                        sortState,
                      )}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className={sortState.key === "tta" ? "is-sorted" : ""}
                      onClick={() => updateSort("tta")}
                    >
                      {sortButtonLabel("Time to Attachment", "tta", sortState)}
                    </button>
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedItems.map((item) => {
                  const name = patientDisplayName((item as any).patient);
                  const postalCode = patientPostalCode(item);
                  const submitted = submittedAt(item);
                  const attachedAt = attachmentCompletedAt(item);

                  return (
                    <tr
                      key={item.requestId}
                      className="ocf-inbox-row ocf-inbox-row--clickable"
                      tabIndex={0}
                      role="button"
                      aria-label={`View details for request #${item.requestId} — ${name}`}
                      onClick={() => openRequestDetails(item.requestId)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openRequestDetails(item.requestId);
                        }
                      }}
                    >
                      <td className="ocf-request-id" data-label="#">
                        #{item.requestId}
                      </td>

                      <td data-label="Name">
                        <div className="ocf-patient-cell">
                          <strong>{name}</strong>
                          <span>{postalCode}</span>
                        </div>
                      </td>

                      <td data-label="Submitted">
                        <span title={formatDateTime(submitted)}>
                          {formatDate(submitted)}
                        </span>
                      </td>

                      <td data-label="Source">
                        <span className={sourceClass(item)}>
                          {sourceLabel(item)}
                        </span>
                      </td>

                      <td data-label="Patient Baseline">
                        <span className={patientBaselineClass(item)}>
                          {patientBaselineLabel(item)}
                        </span>
                      </td>

                      <td data-label="Priority">
                        <span className={priorityClass(item)}>
                          {priorityLabel(item)}
                        </span>
                      </td>

                      <td data-label="Current Status">
                        <span className={statusPillClass(item.status)}>
                          {currentStatusLabel(item.status)}
                        </span>
                      </td>

                      <td data-label="Attachment Date">
                        <span title={formatDateTime(attachedAt)}>
                          {formatDate(attachedAt)}
                        </span>
                      </td>

                      <td data-label="Time to Attachment">{formatTta(item)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <nav
              className="ocf-inbox-pagination"
              aria-label="Request list pagination"
            >
              <p className="ocf-inbox-pagination-summary">
                {formatResultsRange(totalFiltered, currentPageSafe, PAGE_SIZE)}
              </p>

              <div className="ocf-inbox-pagination-controls">
                <button
                  className="ocf-pagination-btn"
                  type="button"
                  onClick={() => setCurrentPage(currentPageSafe - 1)}
                  disabled={currentPageSafe <= 1}
                  aria-label="Previous page"
                >
                  Previous
                </button>

                <div className="ocf-pagination-pages" role="group" aria-label="Pages">
                  {paginationPages.map((page, index) =>
                    page === "ellipsis" ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="ocf-pagination-ellipsis"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        className={`ocf-pagination-btn ocf-pagination-btn--page${
                          page === currentPageSafe ? " is-active" : ""
                        }`}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        aria-current={page === currentPageSafe ? "page" : undefined}
                        aria-label={`Page ${page}`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>

                <button
                  className="ocf-pagination-btn"
                  type="button"
                  onClick={() => setCurrentPage(currentPageSafe + 1)}
                  disabled={currentPageSafe >= totalPages}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </nav>
          ) : null}
          </div>
        )}

        <div className="ocf-inbox-footnote">
          Patient Baseline reflects the patient’s status before applying, based
          on the MOH-style reason captured at intake. Public requests default to
          Routine priority unless a navigator priority is present.
        </div>
      </div>
    </div>
  );
}